<template>
  <BillingSkeletonLoader v-if="loading || isInitializing" />
  <div v-else class="proxima_nova">
    <BillingTrialHeader v-if="status === 'FREE_TRIAL'" :trialDays="trialDays" />

    <div id="manage-billing-v1" class="flex flex-col gap-12 proxima_nova max-w-6xl mx-auto px-4 md:px-4 py-2 pb-28" :class="{ 'pt-16': status === 'FREE_TRIAL' }">
      <div class="flex flex-col gap-6 items-center">
        <div class="flex flex-col gap-1 items-center">
          <span class="text-[30px] font-semibold text_color">Upgrade your plan</span>
          <span class="text-[20px] secondary_color"> Pricing for one-person startups to Fortune 500 enterprises. </span>
        </div>
        <div class="flex flex-col items-center">
          <TabComponent :tabs="filteredBillingTabs" :selectedTab="selectedTab" @tab-change="handleTabChange" />
        </div>
      </div>
      <div class="flex flex-col gap-6">
        <div class="flex flex-row justify-between">
          <div>
            <span class="text-[24px] text_color font-semibold">1. Plan & Users</span>
          </div>
          <div class="flex flex-row gap-2 items-center">
            <span class="font-sm text_color">How many seats do you need?</span>
            <a-tooltip :title="canReduceSeats ? reduceSeatsTooltip : ''" placement="top">
              <span>
                <a-button class="secondary_medium rounded-xl px-2" @click="reduceUserSeats" :disabled="canReduceSeats">
                  <ThemeImage :icon="imageNames.MINUS_ICON" />
                </a-button>
              </span>
            </a-tooltip>
            <span class="text-[24px] text_color font-semibold mx-4">{{ userSeats }}</span>
            <a-button class="gradient_background rounded-xl px-2" @click="increaseUserSeats">
              <ThemeImage :icon="imageNames.ADD_ICON" />
            </a-button>
          </div>
        </div>
        <!--
        <div class="flex flex-row gap-8 w-full" :class="{ 'has-selected-plan': selectedPlanId !== currentPlanId }">
          <PlanCard
            v-for="plan in computedPlans"
            :key="plan.id"
            :plan="plan"
            :isCurrent="plan.id === currentPlanId"
            :isSelected="plan.id === selectedPlanId || (plan.id === currentPlanId && selectedPlanId !== currentPlanId)"
            :ThemeImage="ThemeImage"
            :imageNames="imageNames"
            :checkPaymentDue="showPaymentPopupIfNeeded"
            @select="handlePlanSelection"
          />
        </div>

        <div class="flex flex-row gap-2 items-center justify-center">
          <span class="text-[20px] text_color font-semibold cursor-pointer" @click="showComparison = !showComparison">
            {{ showComparison ? 'Hide plan comparison' : 'Show plan comparison' }}
          </span>
          <button @click="showComparison = !showComparison" class="transition-transform duration-200">
            <ThemeImage :icon="imageNames.DOWN_ARROW_BLACK_ICON" class="w-4 h-4" :style="{ transform: showComparison ? 'rotate(180deg)' : 'rotate(0deg)' }" />
          </button>
        </div>
        <div v-if="showComparison" class="flex flex-col gap-4">
          <PlanComparison />
          <div class="flex flex-row justify-evenly ml-32 mt-4">
            <template v-for="plan in computedPlans" :key="plan.id">
              <button
                :class="[
                  'plan-btn',
                  plan.id === currentPlanId ? 'secondary_medium' : '',
                  plan.id === selectedPlanId && plan.id !== currentPlanId && plan.id === 'free' ? 'primary_medium text-white' : '',
                  plan.id === selectedPlanId && plan.id !== currentPlanId && (plan.id === 'growth' || plan.id === 'starter' || plan.id === 'pro' || plan.id === 'custom')
                    ? 'gradient_background text-white'
                    : '',
                  plan.id !== currentPlanId && plan.id !== selectedPlanId ? 'secondary_medium' : '',
                ]"
                :disabled="plan.id === currentPlanId"
                @click="
                  () => {
                    if (plan.id !== currentPlanId) handlePlanSelection(plan.id);
                  }
                "
              >
                <span v-if="plan.id === currentPlanId">Current Plan</span>
                <span v-else-if="plan.id === selectedPlanId" class="flex items-center justify-center gap-2">
                  <svg v-if="plan.id === 'growth' || plan.id === 'starter' || plan.id === 'pro' || plan.id === 'custom'" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                  Selected Plan
                </span>
                <span v-else>Select Plan</span>
              </button>
            </template>
          </div>
        </div>
        -->
        <!--
          Tier card grid + plan comparison commented out.
          Free / Starter / Growth / Pro tiers are no longer offered in v2 — kept here
          so the markup is recoverable if we ever resurrect the tier-based UI.
        -->
      </div>
      <WhatAreCreditsComponent />
      <div v-if="!(isCreditPack && creditPackGateway === 'razorpay')" class="flex flex-col gap-6">
        <span class="text-[24px] text_color font-semibold">2. Additional Credits</span>
        <CreditsSlider
          ref="creditsSliderRef"
          :plan="computedPlans.find((p) => p.id === selectedPlanId)"
          :billingPeriod="selectedTab"
          :creditPacksData="creditPacksData"
          :loading="loadingCredits"
          :current-plan-id="currentPlanId"
          :selected-plan-id="selectedPlanId"
          :initial-value="initialCreditAddons"
          :plan-credits="computedPlanCredits"
          :user-seats="userSeats"
          :skip-initial-emit="true"
          :reset-slider="resetCreditSlider"
          :check-payment-due="showPaymentPopupIfNeeded"
          @credit-pack-selected="handleCreditPackSelected"
        />
      </div>
      <div class="flex flex-col gap-6 items-center">
        <span class="text-[24px] font-semibold text_color">Trusted by forwarding businesses of all sizes</span>
        <div class="flex flex-row gap-8 items-center w-full">
          <img :src="companyLogoSrc" alt="Company logos" class="w-full object-contain" />
        </div>
      </div>
      <div class="flex flex-col gap-6 items-center">
        <span class="text-[24px] font-semibold text_color">Real Customers, Real Impact</span>
        <div class="flex flex-row gap-6">
          <CustomReviewComponent
            v-for="(review, idx) in customerReviews"
            :key="idx"
            :review="review.review"
            :name="review.name"
            :userName="review.userName"
            :designation="review.designation"
            :avatar="review.avatar"
          />
        </div>
      </div>
      <div class="flex flex-col gap-6 w-full">
        <span class="text-[24px] font-semibold text_color text-center">Know Your Billing</span>
        <BillingFAQAccordion :faqList="billingFAQ" />
      </div>
    </div>

    <!-- Show modals and footer only when not loading -->
    <div v-if="!loading && !isInitializing">
      <CustomModal v-model:visible="showCreditExpirationModal" :title="annualUpsellTitle" width="500px">
        <div class="flex flex-col items-start gap-4">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.coinIcon" alt="coinIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2">
              {{ annualUpsellMessage }}
            </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="cancelBillingChange" :disabled="isModalProcessing">Cancel</a-button>
            <a-button class="primary_medium" @click="confirmBillingChange" :loading="isModalProcessing" :disabled="isModalProcessing">
              {{ isModalProcessing ? 'Upgrading...' : 'Upgrade Now' }}
            </a-button>
          </div>
        </template>
      </CustomModal>

      <CustomModal v-model:visible="showAnnualToMonthlyModal" title="Talk to Sales for Plan Downgrade" width="500px">
        <div class="flex flex-col items-start">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.warningIcon" alt="warningIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2"> Downgrading your plan requires assistance from our Sales Team. Please schedule a call with us to discuss your requirements. </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="cancelAnnualToMonthlyChange">Cancel</a-button>
            <a-button class="primary_medium" @click="talkToSalesForBillingChange">Talk to Sales</a-button>
          </div>
        </template>
      </CustomModal>

      <CustomModal v-model:visible="showCancelTrialModal" title="Cancel Trial and Downgrade to Free Plan" width="500px">
        <div class="flex flex-col items-start gap-4">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.warningIcon" alt="warningIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2">
              You still have <span class="font-semibold text_color">{{ trialDays }} days</span> of Advanced features left. Cancelling your trial will immediately downgrade you to the free plan, remove
              all trial credits and you'll lose access to all advanced features. Are you sure you want to cancel your free trial?
            </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="confirmCancelTrial" :loading="isModalProcessing" :disabled="isModalProcessing">
              {{ isModalProcessing ? 'Canceling...' : 'Cancel Trial' }}
            </a-button>
            <a-button class="primary_medium" @click="cancelTrialModalClose">Keep Trial</a-button>
          </div>
        </template>
      </CustomModal>

      <CustomModal v-model:visible="showPaymentDuesModal" title="Payment Required" width="500px">
        <div class="flex flex-col items-start gap-4">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.warningIcon" alt="warningIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2"> Please clear the pending dues to make changes to your plan </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="closePaymentDuesModal">Cancel</a-button>
            <a-button class="primary_medium" @click="handlePayNow">Pay Now</a-button>
          </div>
        </template>
      </CustomModal>
      <StripeCheckoutModal v-model:visible="showStripeCheckout" :client-secret="stripeClientSecret" :modal-title="checkoutModalTitle" @success="handleCheckoutSuccess" @error="handleCheckoutError" />
      <RazorpayCheckoutModal v-model:visible="showRazorpayCheckout" :order-data="razorpayOrderData" @success="handleRazorpaySuccess" @error="handleRazorpayError" />
    </div>
  </div>
  <BillingFooter
    class="billing-footer-sticky"
    @setup-billing="setupBilling"
    @cancel-trial="handleCancelTrial"
    :billed-monthly="billedMonthly"
    :total-seats-price="displayedTotalSeatsPrice"
    :due-today="dueToday"
    :user-seats="calculatedPaidHumanSeats"
    :credit-packs="selectedCreditPack ? 1 : 0"
    :billing-period="selectedTab"
    :user-seats-amount="userSeatsAmount"
    :credit-packs-amount="creditPacksAmount"
    :total-credits="totalCredits"
    :loading="isCalculatingDue"
    :enable-button="enableButton"
    :current-plan-id="currentPlanId"
    :selected-plan-id="selectedPlanId"
    :selected-credit-pack="selectedCreditPack"
    :is-processing-checkout="isProcessingCheckout"
    :status="status"
    :trial-days="trialDays"
    :currency-symbol="currencySymbol"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import TabComponent from '@/components/manage_billing_components/TabComponent.vue';
import ThemeImage from '@/components/custom/ThemeImage.vue';
import { imageNames } from '@/javascript/imageConstants';
import { billingPlans, customerReviews, billingFAQ, billingTabs } from '@/constants/billingConstants';
import { billingPeriodMap, toFrontendPeriod, getBillingPeriodDisplayText } from '@/utils/billingPeriodUtils';
import PlanCard from '@/components/manage_billing_components/PlanCard.vue';
import PlanComparison from '@/components/manage_billing_components/PlanComparison.vue';
import WhatAreCreditsComponent from '@/components/manage_billing_components/WhatAreCreditsComponent.vue';
import CreditsSlider from '@/components/manage_billing_components/CreditsSlider.vue';
import CustomReviewComponent from '@/components/manage_billing_components/CustomReviewComponent.vue';
import BillingFAQAccordion from '@/components/manage_billing_components/BillingFAQAccordion.vue';
import BillingFooter from '@/components/manage_billing_components/BillingFooter.vue';
import DashboardService from '@/services/DashboardService';
import companyLogoSrc from '@/assets/images/company_logos.svg';
import { message } from 'ant-design-vue';
import axios from 'axios';
import CustomModal from '@/components/custom/CustomModal.vue';
import StripeCheckoutModal from '@/components/manage_billing_components/StripeCheckoutModal.vue';
import RazorpayCheckoutModal from '@/components/manage_billing_components/RazorpayCheckoutModal.vue';
import imagePath from '@/javascript/imagePath';
import BillingTrialHeader from '@/components/manage_billing_components/BillingTrialHeader.vue';
import BillingSkeletonLoader from '@/components/custom/BillingSkeletonLoader.vue';

// State management
const loading = ref(false);
const userName = ref('');
const userSeats = ref(1);
const actualUserCount = ref(1);
const selectedTab = ref('annual');
const currentPlanId = ref('free');
const selectedPlanId = ref('free');
const showComparison = ref(false);
const selectedCreditPack = ref(null);
const creditPacksData = ref({});
const loadingCredits = ref(false);
const trialDays = ref(0);
const initialCreditAddons = ref(0);

// Billing state
const status = ref('');
const currentTotal = ref(0);
const tempTotal = ref(0);
const updateNeeded = ref(false);
const amountDueToday = ref(0);
const totalSeatsPrice = ref(0);
const isCalculatingDue = ref(false);
const billingPeriod = ref('annual');
const originalBillingPeriod = ref('annual');
const workspacePlansAvailable = ref(false);
const workspacePlans = ref([]);
const products = ref([]);
const plans = ref([]);
const superSalesID = ref(0);
const billingCycleEnd = ref('');

// Add refs for calculateDue response values
const calculatedDue = ref(0);
const calculatedTotal = ref(0);
const calculatedHumanSeatsTotal = ref(0);
const calculatedCreditAddonsTotal = ref(0);
const calculatedTotalCredits = ref(0);
const calculatedPaidHumanSeats = ref(1);

// Credit-pack v2 state — populated when the active workspace_plan has a Razorpay /
// Stripe subscription but no pricing tier. We then bypass the tier-based calculate_due
// path and compute footer values locally from /api/v2/pricing.
const isCreditPack = ref(false);
const creditPackGateway = ref('stripe');
const creditPackPricing = ref(null);

// Pricing state
const perHumanSeatAmount = ref(59);
const softwareCreditsPerHumanSeat = ref(2000);
const annualSoftwareCreditsPerHumanSeat = ref(24000);
const annualSuperSalesHumanAmount = ref(0);
const freeHumanSeat = ref(1);
const superSalesHumanSeat = ref(1);
const superSalesHumanAmount = ref(0);
const freePlanCredits = ref(300);
// Add these new variables for starter plan
const starterSeatPrice = ref(9);
const starterSeatAnnualPrice = ref(84);
const starterCreditsPerSeat = ref(300);
const starterAnnualCreditsPerSeat = ref(4100);
// Add pricing for all billing periods - Growth plan
const quarterlySuperSalesHumanAmount = ref(0);
const semiAnnualSuperSalesHumanAmount = ref(0);
const twoYearSuperSalesHumanAmount = ref(0);
const quarterlySoftwareCreditsPerHumanSeat = ref(0);
const semiAnnualSoftwareCreditsPerHumanSeat = ref(0);
const twoYearSoftwareCreditsPerHumanSeat = ref(0);
// Add pricing for all billing periods - Starter plan
const starterSeatQuarterlyPrice = ref(0);
const starterSeatSemiAnnualPrice = ref(0);
const starterSeatTwoYearPrice = ref(0);
const starterQuarterlyCreditsPerSeat = ref(0);
const starterSemiAnnualCreditsPerSeat = ref(0);
const starterTwoYearCreditsPerSeat = ref(0);
// Add pricing for all billing periods - Pro plan
const proSeatPrice = ref(149);
const proSeatAnnualPrice = ref(1428);
const proSeatQuarterlyPrice = ref(402);
const proSeatSemiAnnualPrice = ref(750);
const proSeatTwoYearPrice = ref(2496);
const proCreditsPerSeat = ref(5500);
const proAnnualCreditsPerSeat = ref(66000);
const proQuarterlyCreditsPerSeat = ref(16500);
const proSemiAnnualCreditsPerSeat = ref(33000);
const proTwoYearCreditsPerSeat = ref(132000);

const showStripeCheckout = ref(false);
const stripeClientSecret = ref('');
const checkoutModalTitle = ref('Complete Your Payment');
const isProcessingCheckout = ref(false);
const showRazorpayCheckout = ref(false);
const razorpayOrderData = ref(null);

// Modal states
const showCreditExpirationModal = ref(false);
const billingUpdateResolve = ref(null);
const showAnnualToMonthlyModal = ref(false);
const annualToMonthlyUpdateResolve = ref(null);
const showCancelTrialModal = ref(false);
const showPaymentDuesModal = ref(false);
const paymentRetryLink = ref('');
const isModalProcessing = ref(false); // Separate loading state for modal button

// Add state for already billed seats and new seats
const currentBilledSeats = ref(0); // seats already billed (from backend)
const newSeats = computed(() => Math.max(superSalesHumanSeat.value - currentBilledSeats.value, 0)); // seats user is adding
const seatChanges = computed(() => superSalesHumanSeat.value !== currentBilledSeats.value); // any seat change
const initialSelectedCreditPack = ref(null); // Track initial credit pack
const monthlySelectedCreditPack = ref(null); // Remember monthly credit selection
const resetCreditSlider = ref(false); // Add ref to trigger credit slider reset
const initialMonthlyCredits = ref(0); // Store initial monthly credits from API
const initialAnnualCredits = ref(0); // Store initial annual credits from API

// Add state for enabled billing periods and tiers
const enabledBillingPeriods = ref({
  monthly: true,
  quarterly: true,
  semi_annual: true,
  annual: true,
  two_year: true,
});
const enabledTiers = ref(['custom', 'growth', 'free']);

const isNewUser = computed(() => status.value === 'NEW' || status.value === 'TRIAL');

// USD (Stripe) credit-pack is sold annual-only — USA and other regions get the
// $900/seat/year plan and no monthly option. India (Razorpay/INR) keeps both.
// Existing monthly USD subscribers still see their own period so their current
// plan isn't invisible; the backend monthly path stays live for their renewals.
const isUsdAnnualOnly = computed(() => {
  if (!isCreditPack.value || creditPackGateway.value === 'razorpay') return false;
  const isOnMonthly = originalBillingPeriod.value === 'monthly' && currentBilledSeats.value > 0;
  return !isOnMonthly;
});

// Real annual saving for the credit-pack plan, derived from /api/v2/pricing rather
// than the static 20% on billingTabs (which belongs to the legacy tier catalogue).
// INR: ₹1,500 × 12 vs ₹16,000 ≈ 11%. USD has no monthly plan, so there is nothing
// to compare against and the badge is suppressed.
const creditPackAnnualDiscount = computed(() => {
  if (!isCreditPack.value || isUsdAnnualOnly.value) return 0;
  const currencyKey = creditPackGateway.value === 'razorpay' ? 'inr' : 'usd';
  const monthly = Number(creditPackPricing.value?.[currencyKey]?.monthly?.seat_price) || 0;
  const annual = Number(creditPackPricing.value?.[currencyKey]?.annually?.seat_price) || 0;
  if (!monthly || !annual) return 0;
  return Math.max(0, Math.round((1 - annual / (monthly * 12)) * 100));
});

// Annual upsell modal copy. On the credit-pack plan the saving is whatever the
// backend prices actually work out to (~11% on INR); the legacy tier catalogue
// keeps its 20%. When there is no saving to quote, drop the percentage entirely.
const annualSavingPercent = computed(() => (isCreditPack.value ? creditPackAnnualDiscount.value : 20));

const annualUpsellTitle = computed(() => (annualSavingPercent.value > 0 ? `Switch to Annual & Save ${annualSavingPercent.value}%` : 'Switch to Annual'));

const annualUpsellMessage = computed(() => {
  const saving = annualSavingPercent.value > 0 ? `Save up to ${annualSavingPercent.value}% by switching` : 'Switch';
  return `${saving} to the Annual Plan today. Your subscription will be upgraded immediately, and any unused monthly credits will be canceled.`;
});

// Filter billing tabs based on enabled periods
const filteredBillingTabs = computed(() => {
  const isOnAnnual = (originalBillingPeriod.value === 'annual' || originalBillingPeriod.value === 'annually') && currentBilledSeats.value > 0;
  return billingTabs
    .filter((tab) => !(isUsdAnnualOnly.value && tab.value !== 'annual'))
    .filter((tab) => enabledBillingPeriods.value[tab.value] !== false)
    .map((tab) => {
      // Credit-pack (v2) prices come from the backend, so the static discount on
      // billingTabs no longer describes them.
      if (isCreditPack.value) {
        tab = { ...tab, discount: tab.value === 'annual' ? creditPackAnnualDiscount.value : 0 };
      }
      if (tab.value === 'monthly' && isOnAnnual) {
        return {
          ...tab,
          disabled: true,
          disabledTooltip: 'Plan downgrades require sales team approval. Please contact our support team to switch from annual to monthly.',
        };
      }
      return tab;
    });
});

const hasChanges = computed(() => {
  // Check for seat changes (both additions and removals)
  const hasSeatChanges = seatChanges.value;

  // Check for credit pack changes
  let creditChanges = false;
  if (initialSelectedCreditPack.value && selectedCreditPack.value) {
    // Both have values - compare pack sizes
    creditChanges = initialSelectedCreditPack.value.pack_size !== selectedCreditPack.value.pack_size;
  } else if (!initialSelectedCreditPack.value && selectedCreditPack.value) {
    // No initial pack but now selected one
    creditChanges = selectedCreditPack.value.pack_size > 0;
  } else if (initialSelectedCreditPack.value && !selectedCreditPack.value) {
    // Had initial pack but now removed
    creditChanges = true;
  }

  // Check for billing period changes - only if originalBillingPeriod is set (for PAID users)
  const hasBillingPeriodChanges = originalBillingPeriod.value && effectiveBillingPeriod.value !== originalBillingPeriod.value;

  return hasSeatChanges || creditChanges || hasBillingPeriodChanges;
});

const isDowngrade = computed(() => {
  // Don't check downgrades for free trial users
  if (status.value === 'FREE_TRIAL') {
    return false;
  }

  // Plan tier downgrades - downgrading to free plan requires talking to sales
  if (currentPlanId.value === 'pro' && selectedPlanId.value === 'free') {
    return true;
  }

  if (currentPlanId.value === 'growth' && selectedPlanId.value === 'free') {
    return true;
  }

  if (currentPlanId.value === 'starter' && selectedPlanId.value === 'free') {
    return true;
  }

  // Pro to Growth/Starter downgrades require talking to sales
  if (currentPlanId.value === 'pro' && (selectedPlanId.value === 'growth' || selectedPlanId.value === 'starter')) {
    return true;
  }

  // Growth to Starter is allowed without sales intervention (not treated as downgrade)
  // Starter to Growth is an upgrade (not treated as downgrade)

  // Billing period downgrades for paid users (only if they have an original billing period)
  if (originalBillingPeriod.value && status.value === 'PAID') {
    const billingPeriodHierarchy = {
      two_year: 5,
      annual: 4,
      semi_annual: 3,
      quarterly: 2,
      monthly: 1,
    };

    const currentLevel = billingPeriodHierarchy[originalBillingPeriod.value] || 0;
    const selectedLevel = billingPeriodHierarchy[effectiveBillingPeriod.value] || 0;

    if (selectedLevel < currentLevel) {
      return true;
    }
  }

  return false;
});

const enableButton = computed(() => {
  // v2 credit-pack: enable for first-time purchase (no billed seats yet) and for
  // any seat change against an existing subscription. Pricing is reactive
  // (userSeats × seat_price), so we just need a non-zero seat total.
  if (isCreditPack.value) {
    if (creditPackTotalAmount.value <= 0) return false;
    return currentBilledSeats.value === 0 || seatChanges.value;
  }

  // If user is on free trial - always enable (they can upgrade to paid)
  if (status.value === 'FREE_TRIAL') {
    return true;
  }

  // If current plan is free and selected plan is growth, starter, or pro - enable (upgrade)
  if (currentPlanId.value === 'free' && (selectedPlanId.value === 'growth' || selectedPlanId.value === 'starter' || selectedPlanId.value === 'pro')) {
    return true;
  }

  // If current plan is pro and selected plan is pro with changes - enable
  if (currentPlanId.value === 'pro' && selectedPlanId.value === 'pro') {
    const shouldEnable = hasChanges.value;
    return shouldEnable;
  }

  // If current plan is growth and selected plan is growth with changes - enable
  if (currentPlanId.value === 'growth' && selectedPlanId.value === 'growth') {
    const shouldEnable = hasChanges.value;
    return shouldEnable;
  }

  // If current plan is starter and selected plan is starter with changes - enable
  if (currentPlanId.value === 'starter' && selectedPlanId.value === 'starter') {
    const shouldEnable = hasChanges.value;
    return shouldEnable;
  }

  // If current plan is pro and selected plan is free, growth, or starter - enable (for talk to sales)
  if (currentPlanId.value === 'pro' && (selectedPlanId.value === 'free' || selectedPlanId.value === 'growth' || selectedPlanId.value === 'starter')) {
    return true;
  }

  // If current plan is growth and selected plan is free - enable (for talk to sales)
  if (currentPlanId.value === 'growth' && selectedPlanId.value === 'free') {
    return true;
  }

  // If current plan is starter and selected plan is free - enable (for talk to sales)
  if (currentPlanId.value === 'starter' && selectedPlanId.value === 'free') {
    return true;
  }

  // Plan upgrades to pro
  if ((currentPlanId.value === 'starter' || currentPlanId.value === 'growth') && selectedPlanId.value === 'pro') {
    return true;
  }

  // Plan upgrades/downgrades between starter and growth
  if ((currentPlanId.value === 'starter' && selectedPlanId.value === 'growth') || (currentPlanId.value === 'growth' && selectedPlanId.value === 'starter')) {
    return true;
  }

  // All other cases - disable
  return false;
});

let cancelTokenSource = null;
let watchInitialized = false;
let isInitializing = ref(true);
let calculateDueTimeout = null;
let billingPeriodSetFromAPI = false; // Track if billing period was set from API

const computedPlans = computed(() => {
  // Filter plans based on enabled tiers first
  const filteredPlans = billingPlans.filter((plan) => {
    return enabledTiers.value.includes(plan.id);
  });

  return filteredPlans.map((plan) => {
    if (plan.id === 'growth') {
      if (loading.value || isInitializing.value) {
        if (selectedTab.value === 'monthly') {
          return {
            ...plan,
            price: 59,
            priceText: '$59',
            priceSub: 'Per user, per month\nBilled Monthly',
            credits: '2,000 credits / month',
          };
        } else if (selectedTab.value === 'quarterly') {
          const monthlyRate = Math.round(159 / 3);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 59,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Quarterly',
            credits: '6,000 credits / quarter',
          };
        } else if (selectedTab.value === 'semi_annual') {
          const monthlyRate = Math.round(306 / 6);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 59,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Semi-Annually',
            credits: '12,000 credits / 6 months',
          };
        } else if (selectedTab.value === 'annual') {
          const monthlyRate = Math.round(564 / 12);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 59,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Annually',
            credits: '24,000 credits / year',
          };
        } else if (selectedTab.value === 'two_year') {
          const monthlyRate = Math.round(984 / 24);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 59,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Every 2 Years',
            credits: '48,000 credits / 2 years',
          };
        }
      } else {
        // Use API values
        if (selectedTab.value === 'monthly') {
          return {
            ...plan,
            price: perHumanSeatAmount.value,
            priceText: `$${perHumanSeatAmount.value}`,
            priceSub: 'Per user, per month\nBilled Monthly',
            credits: `${softwareCreditsPerHumanSeat.value.toLocaleString()} credits / month`,
          };
        } else if (selectedTab.value === 'quarterly') {
          const monthlyRate = Math.round(quarterlySuperSalesHumanAmount.value / 3);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: perHumanSeatAmount.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Quarterly',
            credits: `${quarterlySoftwareCreditsPerHumanSeat.value.toLocaleString()} credits / quarter`,
          };
        } else if (selectedTab.value === 'semi_annual') {
          const monthlyRate = Math.round(semiAnnualSuperSalesHumanAmount.value / 6);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: perHumanSeatAmount.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Semi-Annually',
            credits: `${semiAnnualSoftwareCreditsPerHumanSeat.value.toLocaleString()} credits / 6 months`,
          };
        } else if (selectedTab.value === 'annual') {
          const monthlyRate = Math.round(annualSuperSalesHumanAmount.value / 12);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: perHumanSeatAmount.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Annually',
            credits: `${annualSoftwareCreditsPerHumanSeat.value.toLocaleString()} credits / year`,
          };
        } else if (selectedTab.value === 'two_year') {
          const monthlyRate = Math.round(twoYearSuperSalesHumanAmount.value / 24);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: perHumanSeatAmount.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Every 2 Years',
            credits: `${twoYearSoftwareCreditsPerHumanSeat.value.toLocaleString()} credits / 2 years`,
          };
        }
      }
    }

    if (plan.id === 'starter') {
      if (loading.value || isInitializing.value) {
        // Use default values during loading
        if (selectedTab.value === 'monthly') {
          return {
            ...plan,
            price: 9,
            priceText: '$9',
            priceSub: 'Per user, per month\nBilled Monthly',
            credits: '300 credits / month',
          };
        } else if (selectedTab.value === 'quarterly') {
          const monthlyRate = Math.round(24 / 3);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 9,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Quarterly',
            credits: '900 credits / quarter',
          };
        } else if (selectedTab.value === 'semi_annual') {
          const monthlyRate = Math.round(42 / 6);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 9,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Semi-Annually',
            credits: '1,800 credits / 6 months',
          };
        } else if (selectedTab.value === 'annual') {
          const monthlyRate = Math.round(72 / 12);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 9,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Annually',
            credits: '3,600 credits / year',
          };
        } else if (selectedTab.value === 'two_year') {
          const monthlyRate = Math.round(120 / 24);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 9,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Every 2 Years',
            credits: '7,200 credits / 2 years',
          };
        }
      } else {
        // Use API values
        if (selectedTab.value === 'monthly') {
          return {
            ...plan,
            price: starterSeatPrice.value,
            priceText: `$${starterSeatPrice.value}`,
            priceSub: 'Per user, per month\nBilled Monthly',
            credits: `${starterCreditsPerSeat.value.toLocaleString()} credits / month`,
          };
        } else if (selectedTab.value === 'quarterly') {
          const monthlyRate = Math.round(starterSeatQuarterlyPrice.value / 3);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: starterSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Quarterly',
            credits: `${starterQuarterlyCreditsPerSeat.value.toLocaleString()} credits / quarter`,
          };
        } else if (selectedTab.value === 'semi_annual') {
          const monthlyRate = Math.round(starterSeatSemiAnnualPrice.value / 6);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: starterSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Semi-Annually',
            credits: `${starterSemiAnnualCreditsPerSeat.value.toLocaleString()} credits / 6 months`,
          };
        } else if (selectedTab.value === 'annual') {
          const monthlyRate = Math.round(starterSeatAnnualPrice.value / 12);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: starterSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Annually',
            credits: `${starterAnnualCreditsPerSeat.value.toLocaleString()} credits / year`,
          };
        } else if (selectedTab.value === 'two_year') {
          const monthlyRate = Math.round(starterSeatTwoYearPrice.value / 24);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: starterSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Every 2 Years',
            credits: `${starterTwoYearCreditsPerSeat.value.toLocaleString()} credits / 2 years`,
          };
        }
      }
    }

    if (plan.id === 'pro') {
      if (loading.value || isInitializing.value) {
        // Use default values during loading
        if (selectedTab.value === 'monthly') {
          return {
            ...plan,
            price: 149,
            priceText: '$149',
            priceSub: 'Per user, per month\nBilled Monthly',
            credits: '5,500 credits / month',
          };
        } else if (selectedTab.value === 'quarterly') {
          const monthlyRate = Math.round(402 / 3);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 149,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Quarterly',
            credits: '16,500 credits / quarter',
          };
        } else if (selectedTab.value === 'semi_annual') {
          const monthlyRate = Math.round(750 / 6);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 149,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Semi-Annually',
            credits: '33,000 credits / 6 months',
          };
        } else if (selectedTab.value === 'annual') {
          const monthlyRate = Math.round(1428 / 12);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 149,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Annually',
            credits: '66,000 credits / year',
          };
        } else if (selectedTab.value === 'two_year') {
          const monthlyRate = Math.round(2496 / 24);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: 149,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Every 2 Years',
            credits: '132,000 credits / 2 years',
          };
        }
      } else {
        // Use API values
        if (selectedTab.value === 'monthly') {
          return {
            ...plan,
            price: proSeatPrice.value,
            priceText: `$${proSeatPrice.value}`,
            priceSub: 'Per user, per month\nBilled Monthly',
            credits: `${proCreditsPerSeat.value.toLocaleString()} credits / month`,
          };
        } else if (selectedTab.value === 'quarterly') {
          const monthlyRate = Math.round(proSeatQuarterlyPrice.value / 3);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: proSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Quarterly',
            credits: `${proQuarterlyCreditsPerSeat.value.toLocaleString()} credits / quarter`,
          };
        } else if (selectedTab.value === 'semi_annual') {
          const monthlyRate = Math.round(proSeatSemiAnnualPrice.value / 6);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: proSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Semi-Annually',
            credits: `${proSemiAnnualCreditsPerSeat.value.toLocaleString()} credits / 6 months`,
          };
        } else if (selectedTab.value === 'annual') {
          const monthlyRate = Math.round(proSeatAnnualPrice.value / 12);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: proSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Annually',
            credits: `${proAnnualCreditsPerSeat.value.toLocaleString()} credits / year`,
          };
        } else if (selectedTab.value === 'two_year') {
          const monthlyRate = Math.round(proSeatTwoYearPrice.value / 24);
          return {
            ...plan,
            price: monthlyRate,
            originalPrice: proSeatPrice.value,
            priceText: `$${monthlyRate}`,
            priceSub: 'Per user, per month\nBilled Every 2 Years',
            credits: `${proTwoYearCreditsPerSeat.value.toLocaleString()} credits / 2 years`,
          };
        }
      }
    }

    if (plan.id === 'custom') {
      return {
        ...plan,
        price: 0,
        priceText: "Let's Talk",
        priceSub: 'Specific needs tailored to your business usecase',
        credits: 'Custom credits',
        isCustomPlan: true,
      };
    }

    return {
      ...plan,
      price: 0,
      priceText: '$0',
      priceSub: 'Per user, per month',
      credits: loading.value || isInitializing.value ? '300 credits / month' : `${freePlanCredits.value} credits / month`,
    };
  });
});

// Credit-pack helpers: pull current per-seat price/credits from the v2 pricing
// payload based on the IP-resolved gateway and the user's selected billing period.
const creditPackSlice = computed(() => {
  if (!creditPackPricing.value) return null;
  const currencyKey = creditPackGateway.value === 'razorpay' ? 'inr' : 'usd';
  const periodKey = effectiveBillingPeriod.value === 'annual' ? 'annually' : 'monthly';
  return creditPackPricing.value?.[currencyKey]?.[periodKey] ?? null;
});

const creditPackSeatPrice = computed(() => Number(creditPackSlice.value?.seat_price) || 0);
const creditPackTotalAmount = computed(() => userSeats.value * creditPackSeatPrice.value);

// Footer's "User Seats" line reads totalSeatsPrice (a ref); credit-pack overrides it
// with the locally computed total, otherwise falls through to the existing ref value.
const displayedTotalSeatsPrice = computed(() => (isCreditPack.value ? creditPackTotalAmount.value : totalSeatsPrice.value));

// Currency symbol surfaces in the footer; ₹ when Razorpay (Indian IP), $ otherwise.
const currencySymbol = computed(() => (isCreditPack.value && creditPackGateway.value === 'razorpay' ? '₹' : '$'));

// Computed values for billing calculations - use API response values
const billedMonthly = computed(() => {
  if (isCreditPack.value) return creditPackTotalAmount.value;
  // If we have calculated total from calculate_due API, use it
  if (calculatedTotal.value > 0) {
    return calculatedTotal.value;
  }
  // Otherwise, use current total from billing overview
  return currentTotal.value || 0;
});

const dueToday = computed(() => {
  if (isCreditPack.value) {
    const periodChanged = originalBillingPeriod.value && effectiveBillingPeriod.value !== originalBillingPeriod.value;
    if (periodChanged) {
      return creditPackTotalAmount.value;
    }
    const currentlyBilled = (currentBilledSeats.value || 0) * creditPackSeatPrice.value;
    return Math.max(0, creditPackTotalAmount.value - currentlyBilled);
  }
  return calculatedDue.value || 0;
});

const userSeatsAmount = computed(() => {
  if (isCreditPack.value) return creditPackTotalAmount.value;
  return calculatedHumanSeatsTotal.value || 0;
});

const creditPacksAmount = computed(() => {
  // Use calculated credit addons total from API
  return calculatedCreditAddonsTotal.value || 0;
});

const totalCredits = computed(() => {
  // Credit-pack: total = seats × seat_credits from the v2 pricing slice.
  if (isCreditPack.value) {
    const seatCredits = Number(creditPackSlice.value?.seat_credits) || 0;
    return userSeats.value * seatCredits;
  }
  // Show 0 for free plan, otherwise use calculated total credits from API
  if (selectedPlanId.value === 'free') {
    return 0;
  }
  return calculatedTotalCredits.value || 0;
});

const effectiveBillingPeriod = computed(() => {
  return billingPeriodMap[selectedTab.value] || 'monthly';
});

const formattedBillingEndDate = computed(() => {
  if (!billingCycleEnd.value) return '';

  const date = new Date(billingCycleEnd.value);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
});

const effectivePerHumanSeatAmount = computed(() => {
  if (selectedPlanId.value === 'starter') {
    if (selectedTab.value === 'monthly') return starterSeatPrice.value || 0;
    if (selectedTab.value === 'quarterly') return starterSeatQuarterlyPrice.value || 0;
    if (selectedTab.value === 'semi_annual') return starterSeatSemiAnnualPrice.value || 0;
    if (selectedTab.value === 'annual') return starterSeatAnnualPrice.value || 0;
    if (selectedTab.value === 'two_year') return starterSeatTwoYearPrice.value || 0;
    return starterSeatPrice.value || 0;
  }
  if (selectedPlanId.value === 'pro') {
    if (selectedTab.value === 'monthly') return proSeatPrice.value || 0;
    if (selectedTab.value === 'quarterly') return proSeatQuarterlyPrice.value || 0;
    if (selectedTab.value === 'semi_annual') return proSeatSemiAnnualPrice.value || 0;
    if (selectedTab.value === 'annual') return proSeatAnnualPrice.value || 0;
    if (selectedTab.value === 'two_year') return proSeatTwoYearPrice.value || 0;
    return proSeatPrice.value || 0;
  }
  // Growth plan
  if (selectedTab.value === 'monthly') return perHumanSeatAmount.value || 0;
  if (selectedTab.value === 'quarterly') return quarterlySuperSalesHumanAmount.value || 0;
  if (selectedTab.value === 'semi_annual') return semiAnnualSuperSalesHumanAmount.value || 0;
  if (selectedTab.value === 'annual') return annualSuperSalesHumanAmount.value || 0;
  if (selectedTab.value === 'two_year') return twoYearSuperSalesHumanAmount.value || 0;
  return perHumanSeatAmount.value || 0;
});

const effectiveSoftwareCreditsPerHumanSeat = computed(() => {
  if (selectedPlanId.value === 'starter') {
    if (selectedTab.value === 'monthly') return starterCreditsPerSeat.value;
    if (selectedTab.value === 'quarterly') return starterQuarterlyCreditsPerSeat.value;
    if (selectedTab.value === 'semi_annual') return starterSemiAnnualCreditsPerSeat.value;
    if (selectedTab.value === 'annual') return starterAnnualCreditsPerSeat.value;
    if (selectedTab.value === 'two_year') return starterTwoYearCreditsPerSeat.value;
    return starterCreditsPerSeat.value;
  }
  if (selectedPlanId.value === 'pro') {
    if (selectedTab.value === 'monthly') return proCreditsPerSeat.value;
    if (selectedTab.value === 'quarterly') return proQuarterlyCreditsPerSeat.value;
    if (selectedTab.value === 'semi_annual') return proSemiAnnualCreditsPerSeat.value;
    if (selectedTab.value === 'annual') return proAnnualCreditsPerSeat.value;
    if (selectedTab.value === 'two_year') return proTwoYearCreditsPerSeat.value;
    return proCreditsPerSeat.value;
  }
  // Growth plan
  if (selectedTab.value === 'monthly') return softwareCreditsPerHumanSeat.value;
  if (selectedTab.value === 'quarterly') return quarterlySoftwareCreditsPerHumanSeat.value;
  if (selectedTab.value === 'semi_annual') return semiAnnualSoftwareCreditsPerHumanSeat.value;
  if (selectedTab.value === 'annual') return annualSoftwareCreditsPerHumanSeat.value;
  if (selectedTab.value === 'two_year') return twoYearSoftwareCreditsPerHumanSeat.value;
  return softwareCreditsPerHumanSeat.value;
});

const computedPlanCredits = computed(() => {
  if (selectedPlanId.value === 'growth') {
    const credits = effectiveSoftwareCreditsPerHumanSeat.value * userSeats.value;
    return credits || 0;
  }

  if (selectedPlanId.value === 'starter') {
    const credits = effectiveSoftwareCreditsPerHumanSeat.value * userSeats.value;
    return credits || 0;
  }

  if (selectedPlanId.value === 'pro') {
    const credits = effectiveSoftwareCreditsPerHumanSeat.value * userSeats.value;
    return credits || 0;
  }

  return 0;
});

// Helper function to update all pricing values from a plan or workspace plan object
const updatePricingFromPlan = (plan) => {
  if (!plan) return;

  // For WorkspacePlan objects, use PlanID; for Plan objects, use ID/id
  // WorkspacePlan has both ID (workspace plan ID) and PlanID (reference to Plan)
  if (plan.PlanID !== undefined) {
    // This is a WorkspacePlan object - use PlanID
    superSalesID.value = plan.PlanID;
  } else {
    // This is a Plan object - use ID or id
    superSalesID.value = plan.ID || plan.id;
  }
  perHumanSeatAmount.value = Number(plan.human_seat_price || plan.HumanSeatPrice) || 0;
  softwareCreditsPerHumanSeat.value = Number(plan.software_credits_per_human_seat || plan.SoftwareCreditsPerHumanSeat) || 0;
  annualSuperSalesHumanAmount.value = Number(plan.human_seat_annual_price || plan.HumanSeatAnnualPrice) || 0;
  annualSoftwareCreditsPerHumanSeat.value = Number(plan.annual_software_credits_per_human_seat || plan.AnnualSoftwareCreditsPerHumanSeat) || 0;
  freePlanCredits.value = Number(plan.free_plan_credits || plan.FreePlanCredits) || 300;
  freeHumanSeat.value = Number(plan.free_human_seats || plan.FreeHumanSeats) || 0;

  // Growth plan - all billing periods
  quarterlySuperSalesHumanAmount.value = Number(plan.human_seat_quarterly_price || plan.HumanSeatQuarterlyPrice) || 0;
  semiAnnualSuperSalesHumanAmount.value = Number(plan.human_seat_semi_annual_price || plan.HumanSeatSemiAnnualPrice) || 0;
  twoYearSuperSalesHumanAmount.value = Number(plan.human_seat_two_year_price || plan.HumanSeatTwoYearPrice) || 0;
  quarterlySoftwareCreditsPerHumanSeat.value = Number(plan.quarterly_software_credits_per_human_seat || plan.QuarterlySoftwareCreditsPerHumanSeat) || 0;
  semiAnnualSoftwareCreditsPerHumanSeat.value = Number(plan.semi_annual_software_credits_per_human_seat || plan.SemiAnnualSoftwareCreditsPerHumanSeat) || 0;
  twoYearSoftwareCreditsPerHumanSeat.value = Number(plan.two_year_software_credits_per_human_seat || plan.TwoYearSoftwareCreditsPerHumanSeat) || 0;

  // Starter plan - all billing periods
  starterSeatPrice.value = Number(plan.starter_seat_price || plan.StarterSeatPrice) || 9;
  starterSeatAnnualPrice.value = Number(plan.starter_seat_annual_price || plan.StarterSeatAnnualPrice) || 84;
  starterSeatQuarterlyPrice.value = Number(plan.starter_seat_quarterly_price || plan.StarterSeatQuarterlyPrice) || 0;
  starterSeatSemiAnnualPrice.value = Number(plan.starter_seat_semi_annual_price || plan.StarterSeatSemiAnnualPrice) || 0;
  starterSeatTwoYearPrice.value = Number(plan.starter_seat_two_year_price || plan.StarterSeatTwoYearPrice) || 0;
  starterCreditsPerSeat.value = Number(plan.starter_credits_per_seat || plan.StarterCreditsPerSeat) || 300;
  starterAnnualCreditsPerSeat.value = Number(plan.starter_annual_credits_per_seat || plan.StarterAnnualCreditsPerSeat) || 4100;
  starterQuarterlyCreditsPerSeat.value = Number(plan.starter_quarterly_credits_per_seat || plan.StarterQuarterlyCreditsPerSeat) || 0;
  starterSemiAnnualCreditsPerSeat.value = Number(plan.starter_semi_annual_credits_per_seat || plan.StarterSemiAnnualCreditsPerSeat) || 0;
  starterTwoYearCreditsPerSeat.value = Number(plan.starter_two_year_credits_per_seat || plan.StarterTwoYearCreditsPerSeat) || 0;

  // Pro plan - all billing periods
  proSeatPrice.value = Number(plan.pro_seat_price || plan.ProSeatPrice) || 149;
  proSeatAnnualPrice.value = Number(plan.pro_seat_annual_price || plan.ProSeatAnnualPrice) || 1260;
  proSeatQuarterlyPrice.value = Number(plan.pro_seat_quarterly_price || plan.ProSeatQuarterlyPrice) || 402;
  proSeatSemiAnnualPrice.value = Number(plan.pro_seat_semi_annual_price || plan.ProSeatSemiAnnualPrice) || 720;
  proSeatTwoYearPrice.value = Number(plan.pro_seat_two_year_price || plan.ProSeatTwoYearPrice) || 2160;
  proCreditsPerSeat.value = Number(plan.pro_credits_per_seat || plan.ProCreditsPerSeat) || 7500;
  proAnnualCreditsPerSeat.value = Number(plan.pro_annual_credits_per_seat || plan.ProAnnualCreditsPerSeat) || 90000;
  proQuarterlyCreditsPerSeat.value = Number(plan.pro_quarterly_credits_per_seat || plan.ProQuarterlyCreditsPerSeat) || 22500;
  proSemiAnnualCreditsPerSeat.value = Number(plan.pro_semi_annual_credits_per_seat || plan.ProSemiAnnualCreditsPerSeat) || 45000;
  proTwoYearCreditsPerSeat.value = Number(plan.pro_two_year_credits_per_seat || plan.ProTwoYearCreditsPerSeat) || 180000;
};

// API Functions
const fetchUserData = async () => {
  // Prevent multiple simultaneous calls
  if (isApiCallInProgress.value) {
    return;
  }

  isApiCallInProgress.value = true;

  try {
    const workspaceId = localStorage.getItem('billing_workspace_id') || '';
    const res = await DashboardService.userCount();
    if (res?.success) {
      actualUserCount.value = res.user_count;
      userSeats.value = res.user_count;
      superSalesHumanSeat.value = res.user_count;
    }
    const response = await DashboardService.checkUser(workspaceId);
    if (response?.success && response.user) {
      const { first_name, last_name } = response.user;
      userName.value = `${first_name || ''} ${last_name || ''}`.trim();
    }
    const workspaceCurrentResponse = await DashboardService.getWorkspaceCurrent();
    if (workspaceCurrentResponse?.success) {
      // Only update localStorage if the workspace ID is different
      const newWorkspaceId = workspaceCurrentResponse.workspace.ID;
      if (newWorkspaceId !== workspaceId) {
        localStorage.setItem('billing_workspace_id', newWorkspaceId);
        currentWorkspaceId.value = newWorkspaceId;
      } else {
        currentWorkspaceId.value = newWorkspaceId;
      }
    }
  } finally {
    isApiCallInProgress.value = false;
  }
};

const fetchBillingOverview = async () => {
  // Prevent multiple simultaneous calls during workspace changes
  if (isApiCallInProgress.value) {
    return;
  }

  loading.value = true;
  await fetchUserData();
  const response = await DashboardService.getBillingOverview();
  if (response?.success) {
    const { subscription } = response;
    currentTotal.value = subscription.current_total;
    status.value = subscription.status;
    products.value = subscription.products;
    trialDays.value = subscription.free_trial_left || 0;

    // Store billing cycle end date
    if (subscription.billing_cycle && subscription.billing_cycle.end) {
      billingCycleEnd.value = subscription.billing_cycle.end;
    }

    const plansResponse = await DashboardService.getBillingPlans();
    if (plansResponse.success) {
      const plansRes = plansResponse.plans;
      plans.value = plansRes;
      // Backend returns the workspace-specific plan (could be SuperAGI, SuperAGI India, SuperAGI US, etc.)
      const superSales = plans.value[0];
      if (superSales) {
        updatePricingFromPlan(superSales);
      }
    }

    // Simple logic: Use Overview API status to determine current plan
    if (status.value === 'FREE_TRIAL' || status.value === 'PAID') {
      // Use billing overview products data to get current seat count and plan info
      if (products.value && products.value.length > 0) {
        const currentProduct = products.value[0]; // Assuming first product is the main one

        // Determine plan tier from product data
        const planTier = currentProduct.plan_tier || 'growth'; // Default to growth if not specified
        currentPlanId.value = planTier;
        selectedPlanId.value = planTier;

        // Use billing period from product data (available in billing overview response)
        // Priority: product.billing_period > subscription.billing_period > workspace plan BillingPeriod
        let billingPeriodSet = false;
        const backendPeriod = currentProduct.billing_period || subscription.billing_period;
        if (backendPeriod) {
          selectedTab.value = toFrontendPeriod(backendPeriod);
          billingPeriod.value = backendPeriod;
          billingPeriodSet = true;
          billingPeriodSetFromAPI = true; // Mark that billing period was set from API
          // Only set originalBillingPeriod for PAID users, not FREE_TRIAL users
          if (status.value !== 'FREE_TRIAL') {
            originalBillingPeriod.value = backendPeriod;
          }
        }

        // Use seat count from billing overview API
        const currentSeats = currentProduct.human_seats?.count || 1;
        currentBilledSeats.value = currentSeats;

        // Set user seats to current billed seats, but ensure it's at least the actual user count
        superSalesHumanSeat.value = Math.max(currentSeats, actualUserCount.value || 1);
        userSeats.value = superSalesHumanSeat.value;

        // Get additional details from workspace plans for credit addons
        const workspacePlansResponse = await DashboardService.getWorkspacePlans();
        if (workspacePlansResponse?.success && workspacePlansResponse.plans.length > 0) {
          workspacePlansAvailable.value = true;
          workspacePlans.value = workspacePlansResponse.plans;

          // Get the first workspace plan and update pricing from it directly
          const firstWorkspacePlan = workspacePlansResponse.plans[0];
          if (firstWorkspacePlan) {
            // Update all pricing values from the first workspace plan
            updatePricingFromPlan(firstWorkspacePlan);
          }

          const currentPlan = workspacePlansResponse.plans.find((p) => p.PlanID === superSalesID.value);
          if (currentPlan) {
            // Use workspace plan's BillingPeriod if billing period was not set from product/subscription
            if (!billingPeriodSet && currentPlan.BillingPeriod) {
              const workspaceBackendPeriod = currentPlan.BillingPeriod;
              selectedTab.value = toFrontendPeriod(workspaceBackendPeriod);
              billingPeriod.value = workspaceBackendPeriod;
              billingPeriodSet = true;
              billingPeriodSetFromAPI = true; // Mark that billing period was set from API
              if (status.value !== 'FREE_TRIAL') {
                originalBillingPeriod.value = workspaceBackendPeriod;
              }
            }

            // Update free human seats from workspace plan
            freeHumanSeat.value = currentPlan.FreeHumanSeats || 0;

            // Set initial credit addons from workspace plan
            if (currentPlan.credit_addons && currentPlan.credit_addons > 0) {
              initialCreditAddons.value = currentPlan.credit_addons;

              // Store the initial credits based on the current billing period
              if (currentPlan.BillingPeriod === 'annual') {
                // TODO: Check if this is correct
                initialAnnualCredits.value = currentPlan.credit_addons;
              } else {
                initialMonthlyCredits.value = currentPlan.credit_addons;
              }
            }
          }
        }

        // Final fallback: if no billing period was set from product or workspace plan
        if (!billingPeriodSet && subscription.billing_period) {
          const backendPeriod = subscription.billing_period;
          selectedTab.value = toFrontendPeriod(backendPeriod);
          billingPeriod.value = backendPeriod;
          billingPeriodSetFromAPI = true; // Mark that billing period was set from API
          if (status.value !== 'FREE_TRIAL') {
            originalBillingPeriod.value = backendPeriod;
          }
        }
      } else {
        // No products data, fallback to workspace plans
        const workspacePlansResponse = await DashboardService.getWorkspacePlans();
        if (workspacePlansResponse?.success && workspacePlansResponse.plans.length > 0) {
          workspacePlansAvailable.value = true;
          workspacePlans.value = workspacePlansResponse.plans;

          // Get the first workspace plan and update pricing from it directly
          const firstWorkspacePlan = workspacePlansResponse.plans[0];
          if (firstWorkspacePlan) {
            // Update all pricing values from the first workspace plan
            updatePricingFromPlan(firstWorkspacePlan);
          }

          const currentPlan = workspacePlansResponse.plans.find((p) => p.PlanID === superSalesID.value);
          if (currentPlan) {
            // Determine plan tier from workspace plan data
            const planTier = currentPlan.PlanTier || 'growth';
            currentPlanId.value = planTier;
            selectedPlanId.value = planTier;

            // Normalize billing period to ensure consistency
            const backendPeriod = currentPlan.BillingPeriod;
            selectedTab.value = toFrontendPeriod(backendPeriod);
            billingPeriod.value = backendPeriod;
            billingPeriodSetFromAPI = true; // Mark that billing period was set from API
            if (status.value !== 'FREE_TRIAL') {
              originalBillingPeriod.value = backendPeriod;
            }
            currentBilledSeats.value = currentPlan.HumanSeatsCount || 0;

            // Update free human seats from workspace plan
            freeHumanSeat.value = currentPlan.FreeHumanSeats || 0;

            // Use max of workspace plan seats and actual user count
            const workspaceSeats = currentPlan.HumanSeatsCount + currentPlan.FreeHumanSeats || 0;
            superSalesHumanSeat.value = Math.max(workspaceSeats, actualUserCount.value || 1);
            userSeats.value = superSalesHumanSeat.value;

            // Set initial credit addons from workspace plan
            if (currentPlan.credit_addons && currentPlan.credit_addons > 0) {
              initialCreditAddons.value = currentPlan.credit_addons;

              // Store the initial credits based on the current billing period
              if (currentPlan.BillingPeriod === 'annual') {
                initialAnnualCredits.value = currentPlan.credit_addons;
              } else {
                initialMonthlyCredits.value = currentPlan.credit_addons;
              }
            }
          } else {
            // No workspace plan found, default to growth
            currentPlanId.value = 'growth';
            selectedPlanId.value = 'growth';
          }
        } else {
          // No workspace plans available, default to growth
          currentPlanId.value = 'growth';
          selectedPlanId.value = 'growth';
        }
      }
    } else {
      // User is on free plan (FREE_PLAN, NEW, or any other status)
      currentPlanId.value = 'free';
      selectedPlanId.value = 'free';
      selectedTab.value = 'annual';
      currentBilledSeats.value = 0;
      workspacePlans.value = [];
      workspacePlansAvailable.value = false;

      // For free users, use actual user count from API
      superSalesHumanSeat.value = actualUserCount.value || 1;
      userSeats.value = superSalesHumanSeat.value;
      initialCreditAddons.value = 0;
    }
    loading.value = false;
  }
};

const calculateTempTotal = async (shouldCalculateDue = false) => {
  // Calculate the total based on human seats and credit packs only
  const humanTotal = effectivePerHumanSeatAmount.value * superSalesHumanSeat.value;
  const freeHumanSeatDiscount = effectivePerHumanSeatAmount.value * freeHumanSeat.value;
  const creditPackTotal = selectedCreditPack.value ? selectedCreditPack.value.pack_price : 0;

  tempTotal.value = humanTotal - freeHumanSeatDiscount + creditPackTotal;

  if (shouldCalculateDue && !loading.value && !isInitializing.value && !isApiCallInProgress.value) {
    try {
      if (cancelTokenSource) {
        cancelTokenSource.cancel('Request canceled due to new calculation.');
      }

      cancelTokenSource = axios.CancelToken.source();

      const workspacePlan = workspacePlans.value.find((p) => p.PlanID === superSalesID.value);
      const workspacePlanId = workspacePlan?.ID || null;

      // Ensure we're using the current credit pack value - get the most up-to-date value
      const currentCreditAddons = selectedCreditPack.value && selectedCreditPack.value.pack_size !== undefined ? selectedCreditPack.value.pack_size : 0;

      const dueResponse = await DashboardService.calculateDue(
        workspacePlanId ? null : superSalesID.value,
        workspacePlanId,
        superSalesHumanSeat.value,
        0, // Digital seats removed
        effectiveBillingPeriod.value,
        cancelTokenSource.token,
        { credit_addons: currentCreditAddons },
        selectedPlanId.value
      );

      if (dueResponse?.success && dueResponse.data) {
        totalSeatsPrice.value = dueResponse.data.human_seats_total;
        amountDueToday.value = dueResponse.data.due;

        // Store all values from calculateDue response
        calculatedDue.value = dueResponse.data.due;
        calculatedTotal.value = dueResponse.data.total;
        calculatedHumanSeatsTotal.value = dueResponse.data.human_seats_total;
        calculatedCreditAddonsTotal.value = dueResponse.data.credit_addons_total;
        calculatedTotalCredits.value = dueResponse.data.total_credits;
        calculatedPaidHumanSeats.value = dueResponse.data.paid_human_seats;
      } else {
        console.error('calculateDue failed or returned invalid data:', dueResponse);
        // Fallback: calculate due amount locally
        amountDueToday.value = tempTotal.value;
      }
    } catch (error) {
      console.error('Error calculating due:', error);
    }
  }
};

const fetchCreditPacks = async () => {
  loadingCredits.value = true;
  try {
    // Ensure we have a valid plan ID before proceeding
    if (!superSalesID.value) {
      console.warn('superSalesID not available yet, skipping credit packs fetch');
      return;
    }

    // Determine which ID to use based on user type
    let planId = null;
    let workspacePlanId = null;

    // Check if user has workspace plans (existing user)
    if (workspacePlans.value.length > 0) {
      const workspacePlan = workspacePlans.value.find((p) => p.PlanID === superSalesID.value);
      if (workspacePlan) {
        // Existing user - send workspace plan ID
        workspacePlanId = workspacePlan.ID;
      } else {
        // User has workspace plans but not for this plan - send plan ID
        planId = superSalesID.value;
      }
    } else {
      // New user - send plan ID
      planId = superSalesID.value;
    }

    const data = await DashboardService.getCreditsSliderData(planId, workspacePlanId);

    if (data) {
      ['annual', 'monthly', 'quarterly', 'semi_annual', 'two_year'].forEach((period) => {
        if (data[period] && data[period].length > 0) {
          if (data[period][0].pack_size !== 0) {
            data[period].unshift({
              pack_size: 0,
              pack_price: 0,
              billing_period: period,
            });
          }
          data[period].push({
            pack_size: -1,
            pack_price: -1,
            billing_period: period,
            isCustom: true,
          });
        }
      });
      creditPacksData.value = data;

      // Set initial selected credit pack based on workspace plan creditAddons
      if (initialCreditAddons.value > 0) {
        const currentPeriod = billingPeriodMap[selectedTab.value] || 'monthly';
        const matchingPack = data[currentPeriod]?.find((pack) => pack.pack_size === initialCreditAddons.value);
        if (matchingPack) {
          selectedCreditPack.value = {
            ...matchingPack,
            billing_period: currentPeriod,
          };
          // Store as initial for comparison
          initialSelectedCreditPack.value = {
            ...matchingPack,
            billing_period: currentPeriod,
          };
        }
      }
    }
  } catch (error) {
    console.error('Error fetching credit packs:', error);
  } finally {
    loadingCredits.value = false;
  }
};

// Event handlers
const handleTabChange = async (tabValue) => {
  // Only check for billing period downgrades for PAID users, not FREE_TRIAL users
  if (workspacePlansAvailable.value && originalBillingPeriod.value && status.value !== 'FREE_TRIAL') {
    const newBillingPeriod = billingPeriodMap[tabValue] || 'monthly';

    // Check if the new billing period is a downgrade
    const billingPeriodHierarchy = {
      two_year: 5,
      annual: 4,
      semi_annual: 3,
      quarterly: 2,
      monthly: 1,
    };

    const currentLevel = billingPeriodHierarchy[originalBillingPeriod.value] || 0;
    const newLevel = billingPeriodHierarchy[newBillingPeriod] || 0;

    if (newLevel < currentLevel) {
      // This is a billing period downgrade - show modal and prevent tab change
      showAnnualToMonthlyModal.value = true;
      return;
    }

    // Check if upgrading billing period (e.g., monthly to annual)
    if (newLevel > currentLevel) {
      // Check payment due before allowing upgrade
      const paymentOK = showPaymentPopupIfNeeded();
      if (!paymentOK) {
        return; // Payment failed, popup already shown
      }
    }
  }

  selectedTab.value = tabValue;
  billingPeriod.value = billingPeriodMap[tabValue] || 'monthly';

  // Update initialCreditAddons based on the selected tab for existing users
  if (workspacePlansAvailable.value) {
    const backendPeriod = billingPeriodMap[tabValue] || 'monthly';

    // For now, we only have initial credits stored for monthly and annual
    // For other periods, start fresh with no credits
    if (tabValue === 'monthly') {
      initialCreditAddons.value = initialMonthlyCredits.value;
    } else if (tabValue === 'annual') {
      initialCreditAddons.value = initialAnnualCredits.value;
    } else {
      initialCreditAddons.value = 0;
    }

    // Try to find matching credit pack for the selected period
    if (initialCreditAddons.value > 0 && creditPacksData.value[backendPeriod]) {
      const matchingPack = creditPacksData.value[backendPeriod]?.find((pack) => pack.pack_size === initialCreditAddons.value);
      if (matchingPack) {
        selectedCreditPack.value = { ...matchingPack, billing_period: backendPeriod };
      } else {
        selectedCreditPack.value = null;
      }
    } else {
      selectedCreditPack.value = null;
    }
  } else {
    // For new users, reset credit pack when switching tabs
    selectedCreditPack.value = null;
  }

  // Let the watcher handle the calculation
};

const handlePlanSelection = (planId) => {
  if (planId === 'custom') {
    // Open sales meeting link in new tab for custom plan
    window.open('https://meetings.superagi.com/Sales-Demo-Team', '_blank');
    return;
  }
  // Handle regular plan selection
  selectedPlanId.value = planId;
};

const handleCreditPackSelected = (creditPack) => {
  // Handle null/undefined case (when no credit pack is selected)
  if (!creditPack) {
    selectedCreditPack.value = null;
    return;
  }

  // Handle custom credit pack
  if (creditPack.isCustom) {
    selectedCreditPack.value = {
      ...creditPack,
      pack_size: -1,
      pack_price: -1,
      isCustom: true,
    };
  } else {
    // Handle regular credit pack - ensure we use the exact value from the slider
    selectedCreditPack.value = {
      ...creditPack,
      pack_size: creditPack.pack_size, // Explicitly use the current slider value
      pack_price: creditPack.pack_price,
    };
  }

  // Let the watcher handle the calculation
};

const calculateSuperSalesHumanAmount = () => {
  superSalesHumanAmount.value = perHumanSeatAmount.value * superSalesHumanSeat.value;
};

const increaseUserSeats = () => {
  const paymentOK = showPaymentPopupIfNeeded();
  if (!paymentOK) {
    return; // Payment failed, popup already shown
  }

  superSalesHumanSeat.value += 1;
  userSeats.value = superSalesHumanSeat.value;
  calculateSuperSalesHumanAmount();
  // Let the watcher handle the calculation
};

const reduceUserSeats = () => {
  if (superSalesHumanSeat.value > 1 && superSalesHumanSeat.value > actualUserCount.value) {
    superSalesHumanSeat.value -= 1;
    userSeats.value = superSalesHumanSeat.value;
    calculateSuperSalesHumanAmount();
    // Let the watcher handle the calculation
  }
};

const openPaymentRetryModal = (link) => {
  paymentRetryLink.value = link;
  showPaymentDuesModal.value = true;
};

const handlePayNow = () => {
  if (paymentRetryLink.value) {
    window.open(paymentRetryLink.value, '_blank');
  }
  showPaymentDuesModal.value = false;
};

const closePaymentDuesModal = () => {
  showPaymentDuesModal.value = false;
  paymentRetryLink.value = '';
};

const handleCreditPackUpgrade = async () => {
  isProcessingCheckout.value = true;
  let modalOpened = false;
  try {
    const billingPeriodApi = effectiveBillingPeriod.value === 'annual' ? 'annually' : 'monthly';
    const wp = workspacePlans.value && workspacePlans.value[0];
    const hasExistingSub = !!(wp && (wp.RazorpaySubscriptionID || wp.StripeSubscriptionID || wp.razorpay_subscription_id || wp.stripe_subscription_id));

    // Block plan-period downgrades (annual → monthly) only when there IS a prior
    // annual subscription to downgrade from. Stripe / Razorpay don't refund the
    // unused annual portion automatically, so we route those users to sales. For
    // brand-new buyers without a sub, originalBillingPeriod is still the 'annual'
    // default — don't treat their first monthly purchase as a downgrade.
    if (hasExistingSub && originalBillingPeriod.value === 'annual' && effectiveBillingPeriod.value === 'monthly') {
      message.warning('Plan downgrades require sales team approval. Please contact our support team to switch from annual to monthly.');
      isProcessingCheckout.value = false;
      return;
    }

    // Free → paid: provision a brand-new v2 credit-pack subscription. update_seat_subscription
    // rejects this case (it requires an active workspace_plan).
    if (!hasExistingSub) {
      if (creditPackGateway.value === 'razorpay') {
        const resp = await DashboardService.createV2RazorpaySubscription({
          seats: userSeats.value,
          billing_period: billingPeriodApi,
        });
        if (!resp?.success) {
          message.error(resp?.error || 'Failed to create subscription');
          return;
        }
        // RazorpayCheckoutModal expects type==='subscription' to use subscription_id.
        razorpayOrderData.value = { ...resp, type: 'subscription' };
        showRazorpayCheckout.value = true;
        modalOpened = true;
        return;
      }
      const resp = await DashboardService.createV2StripeSubscription({
        seats: userSeats.value,
        billing_period: billingPeriodApi,
        ui_mode: 'embedded',
      });
      if (!resp?.success) {
        message.error(resp?.error || 'Failed to create subscription');
        return;
      }
      const clientSecret = resp.client_secret || resp.clientSecret;
      if (clientSecret) {
        stripeClientSecret.value = clientSecret;
        checkoutModalTitle.value = 'Subscribe — Credit Pack';
        showStripeCheckout.value = true;
        modalOpened = true;
      } else if (resp.url) {
        window.location.href = resp.url;
        modalOpened = true;
      } else {
        message.error('Unexpected response from server. Please refresh and try again.');
      }
      return;
    }

    // Existing sub: seat change → update_seat_subscription (Stripe in-place proration,
    // Razorpay new-mandate-with-old-cancellation).
    const resp = await DashboardService.updateV2SeatSubscription({
      seats: userSeats.value,
      billing_period: billingPeriodApi,
    });
    if (!resp?.success) {
      message.error(resp?.error || 'Failed to update subscription');
      return;
    }
    if (resp.in_place) {
      message.success('Seats updated successfully');
      // Refresh the page state so the new seat count + balance reflect immediately.
      window.location.reload();
      return;
    }
    if (resp.payment_gateway === 'razorpay' && resp.razorpay_subscription_id) {
      razorpayOrderData.value = { ...resp, type: 'subscription' };
      showRazorpayCheckout.value = true;
      modalOpened = true;
      return;
    }
    // Successful response that didn't match any known shape — surface so the user
    // isn't stuck with no feedback.
    message.error('Unexpected response from server. Please refresh and try again.');
  } catch (e) {
    console.error('Credit-pack upgrade failed:', e);
    message.error('Failed to update subscription. Please try again.');
  } finally {
    if (!modalOpened) {
      isProcessingCheckout.value = false;
    }
  }
};

const handleRazorpaySuccess = () => {
  showRazorpayCheckout.value = false;
  razorpayOrderData.value = null;
  message.success('Payment authorized; finalizing your subscription.');
  window.location.reload();
};

const handleRazorpayError = (error) => {
  showRazorpayCheckout.value = false;
  razorpayOrderData.value = null;
  isProcessingCheckout.value = false;
  if (error?.message?.toLowerCase().includes('cancel')) {
    message.info('Payment cancelled');
  } else {
    message.error(error?.message || 'Payment failed. Please try again.');
  }
};

const setupBilling = async () => {
  try {
    // Credit-pack v2 subscriptions: bypass tier-based checkout entirely. The new
    // /update_seat_subscription endpoint hits the right gateway (Stripe in-place
    // proration, Razorpay new-mandate-with-old-cancellation).
    if (isCreditPack.value) {
      await handleCreditPackUpgrade();
      return;
    }
    // Check for any downgrade and show Talk to Sales modal
    if (isDowngrade.value) {
      showAnnualToMonthlyModal.value = true;
      return;
    }

    const isSwitchingToAnnual = originalBillingPeriod.value === 'monthly' && billingPeriod.value === 'annual';

    // Only show modal for PAID users switching from monthly to annual, not for FREE_TRIAL users
    if (isSwitchingToAnnual && workspacePlansAvailable.value && status.value !== 'FREE_TRIAL') {
      const userConfirmed = await showCreditExpirationWarning();
      if (!userConfirmed) {
        isModalProcessing.value = false;
        return;
      }
      // Don't close the modal yet - keep it open with loading state
    }

    // Start checkout processing after modal is handled
    isProcessingCheckout.value = true;

    // Add a small delay to ensure credit slider state is fully synchronized
    await nextTick();

    const workspacePlan = workspacePlans.value.find((p) => p.PlanID === superSalesID.value);
    const workspacePlanId = workspacePlan?.ID || null;

    // Get the most current credit pack value - use the same logic as calculateTempTotal
    let currentCreditAddons = 0;

    // Use the same logic as calculateTempTotal to ensure consistency
    if (selectedCreditPack.value && selectedCreditPack.value.pack_size !== undefined) {
      currentCreditAddons = selectedCreditPack.value.pack_size;
    } else {
      // Fallback to slider component value if selectedCreditPack is not set
      if (creditsSliderRef.value && creditsSliderRef.value.sliderValue !== undefined) {
        currentCreditAddons = creditsSliderRef.value.sliderValue > 0 ? creditsSliderRef.value.sliderValue : 0;
      }
    }

    console.log('Creating Checkout session');

    const response = await DashboardService.createCheckoutSession(
      workspacePlanId ? null : superSalesID.value,
      workspacePlanId,
      userSeats.value,
      0, // Digital seats removed
      billingPeriod.value,
      { credit_addons: currentCreditAddons }, // Always send credit_addons, even if 0
      null,
      'embedded',
      selectedPlanId.value
    );

    if (response?.success) {
      // Check what backend returned
      const clientSecret = response.clientSecret || response.client_secret;

      if (clientSecret) {
        // Backend returned client_secret = Show embedded modal
        stripeClientSecret.value = clientSecret;

        // Set modal title based on action
        if (currentPlanId.value === 'free' && selectedPlanId.value === 'growth') {
          checkoutModalTitle.value = 'Upgrade to Growth Plan';
        } else {
          checkoutModalTitle.value = 'Update Your Billing';
        }

        // Close the confirmation modal and show Stripe checkout
        showCreditExpirationModal.value = false;
        // Keep isProcessingCheckout as true to prevent multiple clicks during checkout

        showStripeCheckout.value = true;
      } else if (response?.url) {
        // Backend returned URL = Redirect to Stripe
        showCreditExpirationModal.value = false;
        window.location.href = response.url;
      } else {
        // Backend returned success without redirect - billing update was successful
        // Close modal and show success message before refresh
        showCreditExpirationModal.value = false;
        message.success('Billing updated successfully!');

        // Refresh the page after a short delay to ensure all data is updated
        setTimeout(() => {
          window.location.reload();
        }, 300); // 0.3 second delay to show success message
      }
    } else {
      // Check for specific error messages in the response
      const errorMessage = response?.error || response?.message || 'Unable to create checkout session';
      if (errorMessage.includes('Payment already in progress')) {
        message.error('Payment already in progress. Please wait and try again.');
      } else {
        message.error(errorMessage);
      }
      // Reset loading states on failure
      isProcessingCheckout.value = false;
      isModalProcessing.value = false;
    }
  } catch (error) {
    // Check for specific "Payment already in progress" error in different possible locations
    const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || '';
    if (errorMessage.includes('Payment already in progress')) {
      message.error('Payment already in progress. Please wait and try again.');
    } else {
      message.error('Error updating billing');
    }
    console.error('Error:', error);
    // Reset loading states on error to allow retry
    isProcessingCheckout.value = false;
    isModalProcessing.value = false;
  } finally {
    // Only reset isModalProcessing if we haven't already reset it in the catch block
    if (isProcessingCheckout.value) {
      isModalProcessing.value = false;
    }
  }
};

const handleCheckoutSuccess = async () => {
  showStripeCheckout.value = false;

  // Refresh all billing data first, then show success message
  loading.value = true;
  try {
    // Fetch billing overview (includes workspace plans)
    await fetchBillingOverview();

    // Fetch credit packs with updated data
    await fetchCreditPacks();

    // Recalculate due amounts with new data
    await calculateTempTotal(true);

    // Show success message after all data is refreshed
    message.success('Payment successful! Your billing has been updated.');
  } catch (error) {
    console.error('Error refreshing billing data:', error);
    message.error('Error updating billing information. Please refresh the page.');
  } finally {
    loading.value = false;
    // Keep isProcessingCheckout as true until page refresh to prevent multiple clicks
  }
};

const handleCheckoutError = (error) => {
  showStripeCheckout.value = false;
  stripeClientSecret.value = ''; // Reset the client secret
  isProcessingCheckout.value = false; // Re-enable button on error
  isModalProcessing.value = false; // Reset modal processing state

  if (error?.message?.includes('cancel')) {
    message.info('Payment cancelled');
  } else {
    message.error('Payment failed. Please try again.');
  }
  console.error('Checkout error:', error);
};

const showCreditExpirationWarning = () => {
  return new Promise((resolve) => {
    billingUpdateResolve.value = resolve;
    showCreditExpirationModal.value = true;
  });
};

const confirmBillingChange = () => {
  isModalProcessing.value = true;

  // When switching from monthly to annual, set annual credits to 0 only if no credits are currently selected
  // Don't reset selectedCreditPack if user has already selected credits in the annual tab
  if (!selectedCreditPack.value || selectedCreditPack.value.pack_size === 0) {
    initialAnnualCredits.value = 0;
    selectedCreditPack.value = null; // Reset credit slider to 0 only if no credits were selected
  }

  if (billingUpdateResolve.value) {
    billingUpdateResolve.value(true);
    billingUpdateResolve.value = null;
  }
};

const cancelBillingChange = () => {
  showCreditExpirationModal.value = false;
  isModalProcessing.value = false;
  if (billingUpdateResolve.value) {
    billingUpdateResolve.value(false);
    billingUpdateResolve.value = null;
  }
};

const showAnnualToMonthlyWarning = () => {
  return new Promise((resolve) => {
    annualToMonthlyUpdateResolve.value = resolve;
    showAnnualToMonthlyModal.value = true;
  });
};

const confirmAnnualToMonthlyChange = () => {
  showAnnualToMonthlyModal.value = false;
  if (annualToMonthlyUpdateResolve.value) {
    annualToMonthlyUpdateResolve.value(true);
    annualToMonthlyUpdateResolve.value = null;
  }
};

const cancelAnnualToMonthlyChange = () => {
  showAnnualToMonthlyModal.value = false;
  // Reset to original billing period tab
  if (originalBillingPeriod.value) {
    selectedTab.value = toFrontendPeriod(originalBillingPeriod.value);
  }
  // Reset to original plan if they were trying to downgrade plan tier
  if (currentPlanId.value !== selectedPlanId.value) {
    selectedPlanId.value = currentPlanId.value;
  }
};

const talkToSalesForBillingChange = () => {
  showAnnualToMonthlyModal.value = false;
  window.open('https://meetings.superagi.com/Sales-Demo-Team', '_blank');
};

const handleCancelTrial = () => {
  showCancelTrialModal.value = true;
};

const cancelTrialModalClose = () => {
  showCancelTrialModal.value = false;
};

const confirmCancelTrial = async () => {
  isModalProcessing.value = true;

  try {
    const workspaceId = localStorage.getItem('billing_workspace_id') || '';
    // Convert workspaceId to number as the API expects
    const workspaceIdNumber = parseInt(workspaceId, 10);
    const response = await DashboardService.changeFreeTrialToFreePlan(workspaceIdNumber);

    if (response?.success) {
      message.success('Trial cancelled successfully!');
      showCancelTrialModal.value = false;

      // Refresh the page to update all data
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      message.error('Failed to cancel trial. Please try again.');
      // Reset loading state on failure
      isModalProcessing.value = false;
    }
  } catch (error) {
    console.error('Error canceling trial:', error);
    message.error('Error canceling trial. Please try again.');
    // Reset loading state on error
    isModalProcessing.value = false;
  }
  // Note: We don't need a finally block here since we handle loading state reset in both success and error cases
};

// Watchers
watch([tempTotal, currentTotal], () => {
  if (!watchInitialized) return;
  updateNeeded.value = tempTotal.value !== currentTotal.value;
});

// Reset loading state when credit expiration modal is closed
watch(showCreditExpirationModal, (newValue) => {
  if (!newValue) {
    // Reset modal processing state when modal is closed
    isModalProcessing.value = false;
    // Only reset checkout processing if we're not in the middle of a checkout
    if (!showStripeCheckout.value) {
      isProcessingCheckout.value = false;
    }
  }
});

// Reset loading state when annual-to-monthly modal is closed
watch(showAnnualToMonthlyModal, (newValue) => {
  if (!newValue) {
    // Reset modal processing state when modal is closed
    isModalProcessing.value = false;
    // Only reset checkout processing if we're not in the middle of a checkout
    if (!showStripeCheckout.value) {
      isProcessingCheckout.value = false;
    }
  }
});

// Single consolidated watcher for all billing changes
watch(
  [superSalesHumanSeat, selectedCreditPack, selectedPlanId, selectedTab],
  () => {
    if (!loading.value && !isInitializing.value && !isApiCallInProgress.value && !isProcessingCheckout.value) {
      if (
        currentPlanId.value === 'growth' ||
        selectedPlanId.value === 'growth' ||
        selectedPlanId.value === 'starter' ||
        currentPlanId.value === 'starter' ||
        selectedPlanId.value === 'pro' ||
        currentPlanId.value === 'pro'
      ) {
        // Clear any existing timeout to prevent multiple rapid calls
        if (calculateDueTimeout) {
          clearTimeout(calculateDueTimeout);
        }

        // Show loader immediately
        isCalculatingDue.value = true;
        const startTime = Date.now();
        const minLoaderDuration = 700; // 0.8 seconds

        // Debounce the API call to prevent multiple rapid successive calls
        calculateDueTimeout = setTimeout(async () => {
          try {
            // Double-check API call is not in progress during the debounce period
            if (!isApiCallInProgress.value && !isProcessingCheckout.value) {
              await calculateTempTotal(true);
            } else {
              console.error('API call in progress, skipping calculateTempTotal');
            }
          } finally {
            // Ensure minimum loader duration of 0.8 seconds
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minLoaderDuration - elapsedTime);

            setTimeout(() => {
              isCalculatingDue.value = false;
            }, remainingTime);
          }
        }, 500); // Increased debounce to 500ms for better stability
      }
    }
  },
  { deep: true }
);

// Lifecycle
onMounted(async () => {
  try {
    // Initialize workspace tracking from localStorage
    const initialWorkspaceId = localStorage.getItem('billing_workspace_id') || '';
    currentWorkspaceId.value = initialWorkspaceId;

    // Initialize calculations
    calculateSuperSalesHumanAmount();

    // Fetch billing overview first (sets superSalesID)
    await fetchBillingOverview();

    // Fetch enabled billing periods and tiers
    await fetchEnabledBillingPeriodsAndTiers();

    // Check payment status once and cache it
    await checkAndCachePaymentStatus();

    // Then fetch credit packs (needs superSalesID)
    await fetchCreditPacks();

    // Fetch /api/v2/pricing + the IP-resolved gateway. Without this, a free-plan
    // v2 user sees a frozen $0 footer because the tier-based calculate_due watcher
    // is gated on growth/starter/pro.
    try {
      const accountResp = await DashboardService.getOrCreateAccount();
      const pricing = await DashboardService.getV2Pricing();
      if (pricing?.inr && pricing?.usd) {
        creditPackPricing.value = pricing;
        if (accountResp?.payment_gateway) creditPackGateway.value = accountResp.payment_gateway;
        // Credit-pack vs tier-based is a property of the PLAN, not the billing
        // version — a workspace can be v2 AND tier-based. Mirror the backend guard
        // (UpdateV2SeatSubscription: PricingTierID != nil && != 0 => tier-based) and
        // Rails' is_credit_pack, otherwise tier-based workspaces take the seat-only
        // enableButton branch (credit changes never enable the button) and get
        // billedMonthly = seats x price, which silently drops the credit addon.
        // No active plan yet (free/new) => no tier, so treat as credit-pack.
        const activeWp = workspacePlans.value?.find((p) => p.PlanID === superSalesID.value) ?? workspacePlans.value?.[0];
        isCreditPack.value = !activeWp || activeWp.PricingTierID == null || activeWp.PricingTierID === 0;
        // BillingFooter hard-codes "0" whenever selectedPlanId === 'free' (see
        // BillingFooter.vue:22/29/43). Bump both ids off 'free' so the footer
        // renders the live credit-pack total. The tier UI is commented out, so
        // 'growth' is just a display token here — no tier semantics attached.
        if (selectedPlanId.value === 'free') selectedPlanId.value = 'growth';
        if (currentPlanId.value === 'free') currentPlanId.value = 'growth';
      } else {
        console.warn('v2 pricing unavailable; footer will stay at $0');
      }
    } catch (e) {
      console.error('Credit-pack init failed:', e);
    }

    isInitializing.value = false;

    if (
      currentPlanId.value === 'growth' ||
      selectedPlanId.value === 'growth' ||
      currentPlanId.value === 'starter' ||
      selectedPlanId.value === 'starter' ||
      currentPlanId.value === 'pro' ||
      selectedPlanId.value === 'pro'
    ) {
      isCalculatingDue.value = true;
      const startTime = Date.now();
      const minLoaderDuration = 700; // 0.8 seconds

      await calculateTempTotal(true);

      // Ensure minimum loader duration of 0.8 seconds
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoaderDuration - elapsedTime);

      setTimeout(() => {
        isCalculatingDue.value = false;
      }, remainingTime);
    }

    await nextTick();
    watchInitialized = true;
  } catch (error) {
    console.error('Error initializing billing page:', error);
    message.error('Error loading billing information. Please refresh the page.');
    isInitializing.value = false;
  }
});

onBeforeUnmount(() => {
  // Clean up any existing timeouts
  if (calculateDueTimeout) {
    clearTimeout(calculateDueTimeout);
  }
});

// Add computed property for reduce button disabled state
const canReduceSeats = computed(() => {
  // Can't go below (1 + freeHumanSeats) OR below actual user count
  return superSalesHumanSeat.value <= freeHumanSeat.value + 1 || superSalesHumanSeat.value <= actualUserCount.value;
});

// Add computed property for tooltip message
const reduceSeatsTooltip = computed(() => {
  if (superSalesHumanSeat.value <= freeHumanSeat.value + 1) {
    const freeSeatsText = freeHumanSeat.value > 0 ? `You have ${freeHumanSeat.value} free seat${freeHumanSeat.value > 1 ? 's' : ''}. ` : '';
    return `${freeSeatsText}Minimum 1 seat required`;
  }
  if (superSalesHumanSeat.value + freeHumanSeat.value <= actualUserCount.value) {
    return 'To decrease seats, remove users from the workspace';
  }
  return '';
});

// Add workspace tracking
const currentWorkspaceId = ref('');
const isWorkspaceChanging = ref(false);
const isApiCallInProgress = ref(false);

// Add ref for credits slider component
const creditsSliderRef = ref(null);

const paymentDueStatus = ref(null);
const paymentRetryLinkCached = ref('');

const checkAndCachePaymentStatus = async () => {
  const workspaceId = localStorage.getItem('billing_workspace_id');
  const workspaceIdNumber = parseInt(workspaceId, 10);

  try {
    const paymentResponse = await DashboardService.checkPaymentDue(workspaceIdNumber);
    paymentDueStatus.value = paymentResponse;

    if (paymentResponse?.success !== undefined && paymentResponse?.payment_failed === true && paymentResponse?.retry_link !== '') {
      paymentRetryLinkCached.value = paymentResponse.retry_link;
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error checking payment due:', error);
    return true;
  }
};

const showPaymentPopupIfNeeded = () => {
  if (paymentDueStatus.value?.success !== undefined && paymentDueStatus.value?.payment_failed === true && paymentRetryLinkCached.value !== '') {
    console.log('Payment failed - showing retry modal and returning false');
    console.log('Retry link:', paymentRetryLinkCached.value);
    openPaymentRetryModal(paymentRetryLinkCached.value);
    return false;
  }
  return true;
};

const fetchEnabledBillingPeriodsAndTiers = async () => {
  try {
    // Fetch enabled billing periods
    const periodsResponse = await DashboardService.getEnabledBillingPeriods();
    if (periodsResponse?.success && periodsResponse?.enabled_periods) {
      enabledBillingPeriods.value = periodsResponse.enabled_periods;

      // Only check and override tab if billing period was NOT set from API
      // If billing period was set from API, respect it and don't override
      if (!billingPeriodSetFromAPI) {
        // Convert frontend tab value to backend period key for checking
        // enabled_periods might use backend keys (annual, quarterly, etc.) or frontend keys (annual, etc.)
        // Check both to be safe
        const backendPeriodKey = billingPeriodMap[selectedTab.value];
        const isTabEnabledBackend = backendPeriodKey ? enabledBillingPeriods.value[backendPeriodKey] !== false : false;
        const isTabEnabledFrontend = enabledBillingPeriods.value[selectedTab.value] !== false;
        const isTabEnabled = isTabEnabledBackend || isTabEnabledFrontend;

        // If current selected tab is not enabled, switch to the first enabled tab
        if (filteredBillingTabs.value.length > 0 && !isTabEnabled) {
          selectedTab.value = filteredBillingTabs.value[0].value;
          billingPeriod.value = billingPeriodMap[selectedTab.value] || 'monthly';
        }
      }
    }

    // Fetch enabled tiers
    const tiersResponse = await DashboardService.getEnabledTiers();
    if (tiersResponse?.success && tiersResponse?.enabled_tiers) {
      enabledTiers.value = tiersResponse.enabled_tiers;
    }
  } catch (error) {
    console.error('Error fetching enabled billing periods and tiers:', error);
  }
};

// Update button logic
</script>

<style scoped>
.plan-btn {
  width: fit-content;
  border-radius: 12px;
  padding: 7px 24px;
  font-size: 16px;
  font-weight: 600;
}
.plan-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.billing-footer-sticky {
  position: fixed;
  left: 225px;
  width: calc(100vw - 225px);
  bottom: 0;
  z-index: 10;
  height: 90px;
  margin-left: 0 !important;
}
</style>
